import {
  onBeforeMount,
  watch as vueWatch,
  watchEffect,
  reactive,
  computed,
  ref,
  toValue,
  type ComputedRef,
  type WatchStopHandle,
  type WatchSource,
  type Ref,
  type WatchOptions,
  toRaw,
} from 'vue';
import { sleep } from './utils';
import axios from 'axios';
import { isDefined } from '@vueuse/shared';
import isEqual from 'lodash-es/isEqual';

/** Stati possibili per un asyncComputed durante il suo ciclo di vita */
export enum AsyncComputedState {
  /** Stato iniziale, mai eseguito */
  Pending = 'pending',
  /** In aggiornamento (ricalcolo in corso) */
  Updating = 'updating',
  /** Completato con successo */
  Success = 'success',
  /** Errore durante l'esecuzione */
  Error = 'error',
}

/**
 * Oggetto reattivo che rappresenta lo stato di un asyncComputed con metodi di controllo
 */
export interface AsyncComputedStateObject {
  /** Indica se la funzione async è stata eseguita almeno una volta */
  hasEverRun: boolean;
  /** Eventuale errore catturato durante l'esecuzione */
  error?: unknown;
  /** Stato corrente dell'asyncComputed */
  state: AsyncComputedState;
  /** Computed che indica se lo stato è Success */
  success: boolean;
  /** Computed che indica se lo stato è Pending */
  pending: boolean;
  /** Computed che indica se lo stato è Updating */
  updating: boolean;
  /** Promise che si risolve quando l'asyncComputed esce dallo stato Pending */
  ready: () => Promise<void>;
  /** Forza un aggiornamento del valore rieseguendo la funzione getter */
  update: () => Promise<void>;
}

/**
 * Oggetto restituito da asyncComputed con il risultato e lo stato
 * @template T - Tipo del valore computato
 */
export interface AsyncComputed<T> {
  /** Computed reattivo con il risultato della funzione async */
  result: ComputedRef<T>;
  /** Oggetto reattivo con stato e metodi di controllo */
  state: AsyncComputedStateObject;
}

type AsyncComputedGetFn<T, D = T, V = any> = (prev: T | D, watchVal?: V, prevWatchVal?: V) => MaybePromise<T | D>;

interface AsyncComputedDefinition<T, D = T, V = any> {
  get: AsyncComputedGetFn<T, D>;
  default?: D;
  lazy?: boolean;
  debounce?: number;
  options?: WatchOptions | undefined;
  watch?: WatchSource<V>;
}

/**
 * Type helper per estrarre il tipo resolved da un AsyncComputed, Promise, o funzione
 * @template T - Tipo da cui estrarre il valore resolved
 */
export type ResolvedComputedPromise<T> =
  T extends AsyncComputedDefinition<infer U, infer D>
    ? D extends never[]
      ? Awaited<U>
      : Awaited<U> | D
    : T extends AsyncComputedDefinition<infer U>
      ? Awaited<U>
      : T extends AsyncComputed<infer U>
        ? U
        : T extends Promise<infer U>
          ? U
          : T extends () => infer U
            ? U
            : never;

type AsyncComputedOptions = Pick<WatchOptions, 'deep'> & {
  debounce?: number;
  lazy?: boolean;
};

/**
 * Helper per creare asyncComputed che ritorna un array con default array vuoto
 * @template T - Tipo degli elementi dell'array
 * @template V - Tipo del valore watchato
 * @param func - Funzione async che ritorna l'array
 * @param watch - Sorgente reattiva da watchare per triggare ricalcoli
 * @param options - Opzioni (debounce, lazy, deep)
 * @returns AsyncComputed configurato per array con default []
 * @remarks debounce funziona meglio in combinazione con watch
 */
export function asyncComputedList<T, V = unknown>(
  func: AsyncComputedGetFn<T[]>,
  watch?: WatchSource<V>,
  options?: AsyncComputedOptions | undefined,
): AsyncComputed<T[]> {
  const def: ReturnType<Awaited<typeof func>> = [];
  return asyncComputed(func, def, watch, options);
}

/**
 * Crea un computed reattivo che supporta funzioni async con gestione stato e aggiornamenti automatici
 * @template T - Tipo del valore computato
 * @template D - Tipo del valore di default
 * @template V - Tipo del valore watchato
 * @param func - Funzione (sync o async) che calcola il valore, riceve (prev, watchVal, prevWatchVal)
 * @param def - Valore di default usato prima che la funzione completi la prima esecuzione
 * @param watch - Sorgente reattiva da watchare per triggare ricalcoli automatici
 * @param options - Opzioni di configurazione
 * @param options.lazy - Se true, non esegue subito ma solo al primo accesso (default: false)
 * @param options.debounce - Millisecondi di debounce tra ricalcoli (default: 0)
 * @param options.deep - Se true, watch in modalità deep (default: false)
 * @returns AsyncComputed con result (computed reattivo) e state (oggetto stato/controllo)
 */
export function asyncComputed<T, D = T, V = unknown>(
  func: AsyncComputedGetFn<T, D>,
  def?: D,
  watch?: WatchSource<V>,
  options?: AsyncComputedOptions | undefined,
): AsyncComputed<T | D>;
export function asyncComputed(
  func: AsyncComputedGetFn<any>,
  def?: any,
  watch?: WatchSource<unknown>,
  options: AsyncComputedOptions = {},
): AsyncComputed<unknown> {
  const lazy = options.lazy ?? false;
  const debounce = options.debounce ?? 0;
  const watchDeep = options.deep ?? false;

  const ctx: {
    lastCallee: symbol | null;
    stopLast: WatchStopHandle | null;
    hasEverRequested: boolean;
    resultData: Ref<any>;
    prevValue: any;
  } = {
    lastCallee: null,
    stopLast: null,
    hasEverRequested: false,
    resultData: ref(def),
    prevValue: def,
  };

  let resolveReadyPromise: undefined | (() => void) = undefined;
  const readyPromise = new Promise<void>(resolve => (resolveReadyPromise = resolve));

  const stateObject: AsyncComputedStateObject = reactive({
    hasEverRun: false,
    state: AsyncComputedState.Pending,
    success: computed(() => stateObject.state === AsyncComputedState.Success) as unknown as boolean,
    pending: computed(() => stateObject.state === AsyncComputedState.Pending) as unknown as boolean,
    updating: computed(() => stateObject.state === AsyncComputedState.Updating) as unknown as boolean,
    async ready() {
      if (stateObject.state !== AsyncComputedState.Pending) {
        return;
      }
      return await readyPromise;
    },
    error: null,

    update() {
      return new Promise<void>((resolve, reject) => {
        ctx.hasEverRequested = true;
        if (ctx.stopLast) {
          ctx.stopLast();
          ctx.stopLast = null;
        }

        const watchFn = async (watchVal?: unknown, prevWatchVal?: unknown) => {
          if (resolveReadyPromise === undefined && prevWatchVal !== undefined && isEqual(toRaw(watchVal), toRaw(prevWatchVal))) {
            resolve();
            return;
          }

          const me = Symbol('retry');
          const firstCallee = ctx.lastCallee === null;

          // debouncing with watchEffect should not drop first call
          if (!debounce || watch) {
            ctx.lastCallee = me;
          }

          if (debounce) {
            // we cannot short-circuit first call with watchEffects otherwise no "effects" would be tracked in later calls
            if (watch || !firstCallee) {
              await sleep(debounce);
            }
            if (watch && ctx.lastCallee !== me) {
              resolve();
              return;
            }
          }

          if (debounce && !watch) {
            ctx.lastCallee = me;
          }

          setState(AsyncComputedState.Updating);

          const onFullfilled = (value: any) => {
            resolveReadyPromise?.();
            resolveReadyPromise = undefined;

            if (ctx.lastCallee === me) {
              setState(AsyncComputedState.Success);
              ctx.resultData.value = value;
              ctx.prevValue = value;
              stateObject.hasEverRun = true;
            }
            resolve();
          };

          const onRejected = (error: any) => {
            resolveReadyPromise?.();
            resolveReadyPromise = undefined;

            if (ctx.lastCallee === me) {
              setState(AsyncComputedState.Error, error);
              stateObject.hasEverRun = true;
            }
            if (axios.isAxiosError(error) && error.response && [401, 403, 404, 410].includes(error.response.status)) {
              resolve();
            } else {
              reject(error);
            }
          };

          let maybePromise;
          try {
            maybePromise = func(ctx.prevValue, watchVal, prevWatchVal);
          } catch (error) {
            onRejected(error);
            return;
          }

          if (maybePromise instanceof Promise) {
            maybePromise.then(onFullfilled, onRejected);
          } else {
            onFullfilled(maybePromise);
          }
        };

        ctx.stopLast = isDefined(watch) ? vueWatch(watch, watchFn, { immediate: true, deep: watchDeep }) : watchEffect(watchFn);
      });
    },
  });

  function setState(state: AsyncComputedState, error: any = null) {
    stateObject.state = state;
    stateObject.error = error;
  }

  const result = computed(() => {
    if (!ctx.hasEverRequested) {
      stateObject.update();
    }
    return toValue(ctx.resultData);
  });

  if (!lazy) {
    onBeforeMount(stateObject.update);
  }

  return {
    result,
    state: stateObject,
  };
}
